import React, { useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { useMerchant } from '../../../contexts/MerchantContext';
import { useCanManagePortalUsers, useIsSkipifyAdmin } from '../../../hooks';
import { DataTable, Column } from '../../../common/DataTable';
import { Pagination } from '../../../common/DataTable/Pagination';
import { ActionsPopover } from '../../../common/ActionsPopover';
import { RefreshButton } from '../../Paylinks/components/RefreshButton';
import { SearchTextField } from '../../Paylinks/components';
import { Button } from '../../../components/core';
import { ActionDialogModal } from '../../Paylinks/components/ActionDialog/modal';
import {
  useCreatePortalUserMutation,
  useDeletePortalUserMutation,
  useLazyGetPortalUserQuery,
  useListPortalUsersForMerchantQuery,
  useResendPortalUserAccessEmailMutation,
  useRevokeMerchantAccessMutation,
  useUpdatePortalUserMutation,
} from '../../../store/modules/merchant/api';
import {
  CreatePortalUserBody,
  PortalUserListItem,
  PortalUserSortQuery,
  UpdatePortalUserBody,
} from '../../../store/modules/merchant/types';
import { toast } from '../../../util/toast';
import {
  getResendPortalUserEmailActionLabel,
  getResendPortalUserEmailSuccessMessage,
} from '../../PortalUsers/portalUserEmailActions';
import { getSharedPortalUserColumns } from '../../PortalUsers/portalUserTableColumns';
import { PortalUserFormDialog } from '../../PortalUsers/components/PortalUserFormDialog';
import { useAssignablePortalUserRoles } from '../../PortalUsers/portalUserRoleCatalog';
import { GrantMerchantAccessDialog } from './GrantMerchantAccessDialog';
import { shouldBlockPortalUserSelfManagement } from '../../PortalUsers/portalUserSelfManagement';
import {
  getApiErrorMessage,
  getRevokeMerchantAccessHelpingText,
} from '../../PortalUsers/portalUserMerchantAccess';
import { PORTAL_USER_MERCHANT_USERS_ROSTER_HELPER } from '../../PortalUsers/portalUserMerchantAccessCopy';

export const MerchantUsersTable = () => {
  const { merchantId, merchantName, merchantUserId } = useMerchant();
  const isSkipifyAdmin = useIsSkipifyAdmin();
  const canManagePortalUsers = useCanManagePortalUsers();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PortalUserListItem>();
  const [deleteUserId, setDeleteUserId] = useState<string>();
  const [isGrantDialogOpen, setIsGrantDialogOpen] = useState(false);
  const [sortQuery, setSortQuery] = useState<PortalUserSortQuery>({});
  const tableRef = useRef<HTMLDivElement>(null);

  const initialMerchants = useMemo(
    () => (merchantId && merchantName ? [{ merchantId, name: merchantName }] : []),
    [merchantId, merchantName],
  );

  const roles = useAssignablePortalUserRoles();
  const {
    data: response,
    isFetching,
    refetch,
  } = useListPortalUsersForMerchantQuery(
    {
      merchantId: merchantId ?? '',
      pageNumber,
      pageSize,
      ...(searchQuery.trim() ? { searchQuery: searchQuery.trim() } : {}),
      ...sortQuery,
    },
    { skip: !merchantId },
  );
  const [getPortalUser, { data: selectedUserDetail }] = useLazyGetPortalUserQuery();
  const [createPortalUser] = useCreatePortalUserMutation();
  const [updatePortalUser] = useUpdatePortalUserMutation();
  const [resendAccessEmail] = useResendPortalUserAccessEmailMutation();
  const [revokeMerchantAccess, { isLoading: isRevoking }] = useRevokeMerchantAccessMutation();
  const [deletePortalUser, { isLoading: isDeleting }] = useDeletePortalUserMutation();

  const openEditDialog = async (userId: string) => {
    setSelectedUserId(userId);
    try {
      await getPortalUser(userId).unwrap();
      setFormMode('edit');
    } catch {
      toast.error('Unable to load user details.');
    }
  };

  const handleResendEmail = async (user: PortalUserListItem) => {
    try {
      await resendAccessEmail(user.userId).unwrap();
      toast.success(getResendPortalUserEmailSuccessMessage(user.hasLoggedIn));
    } catch {
      toast.error('Unable to send email. Please try again.');
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget || !merchantId) return;

    try {
      await revokeMerchantAccess({
        userId: revokeTarget.userId,
        merchantId,
      }).unwrap();
      toast.success('Merchant access removed.');
      if (selectedUserId === revokeTarget.userId) {
        setSelectedUserId(undefined);
      }
      setRevokeTarget(undefined);
      refetch();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to remove merchant access. Please try again.'));
      setRevokeTarget(undefined);
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;

    try {
      await deletePortalUser(deleteUserId).unwrap();
      toast.success('User deleted.');
      if (selectedUserId === deleteUserId) {
        setSelectedUserId(undefined);
        setFormMode(null);
      }
      setDeleteUserId(undefined);
      refetch();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to delete user. Please try again.'));
    }
  };

  const columns: Column<PortalUserListItem>[] = [
    ...getSharedPortalUserColumns(),
    {
      id: 'actions',
      headerName: 'Actions',
      align: 'center',
      headerAlign: 'center',
      renderCell: (row) => {
        const blockSelf = shouldBlockPortalUserSelfManagement(
          row.userId,
          merchantUserId,
          isSkipifyAdmin,
        );
        return (
          <ActionsPopover
            ariaLabel="Merchant user actions"
            actions={[
              {
                label: 'Edit',
                onClick: () => openEditDialog(row.userId),
                disabled: blockSelf,
              },
              {
                label: getResendPortalUserEmailActionLabel(row.hasLoggedIn),
                onClick: () => handleResendEmail(row),
                disabled: blockSelf,
              },
              {
                label: 'Remove access',
                onClick: () => setRevokeTarget(row),
                disabled: blockSelf,
              },
              {
                label: 'Delete',
                onClick: () => setDeleteUserId(row.userId),
                disabled: blockSelf || (!!merchantUserId && row.userId === merchantUserId),
              },
            ]}
          />
        );
      },
      minWidth: 80,
      maxWidth: 80,
    },
  ];

  return (
    <>
      <Stack gap={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2,
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', lg: 'center' },
          }}
        >
          <Stack gap={0.5}>
            <Stack direction="row" gap={2} alignItems="center">
              <Typography component="h2" fontWeight="semibold" variant="xl">
                Users with direct access
              </Typography>
              <RefreshButton
                onClick={() => {
                  setSearchResetKey((key) => key + 1);
                  refetch();
                }}
                disabled={isFetching}
              />
            </Stack>
            <Typography sx={{ color: 'text.secondary', fontSize: 13, maxWidth: 720 }}>
              {PORTAL_USER_MERCHANT_USERS_ROSTER_HELPER}
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            {canManagePortalUsers ? (
              <Button text="Grant access" onClick={() => setIsGrantDialogOpen(true)} />
            ) : null}
            <Button text="Add user" onClick={() => setFormMode('add')} />
          </Stack>
        </Box>
        <SearchTextField
          label="Search by email or name"
          defaultValue={searchQuery}
          resetKey={searchResetKey}
          minSearchLength={3}
          disabled={isFetching}
          onSearch={(value) => {
            if (isFetching) return;
            setPageNumber(1);
            setSearchQuery(value);
          }}
        />
        <DataTable<PortalUserListItem>
          rows={response?.data ?? []}
          columns={columns}
          getRowId={(row) => row.userId}
          isLoading={isFetching}
          loadingVariant="spinner"
          loadingMessage="Loading users…"
          pageSize={pageSize}
          sort={sortQuery}
          sortDisabled={isFetching}
          setSort={(query) => {
            if (isFetching) return;
            setPageNumber(1);
            setSortQuery(query as PortalUserSortQuery);
          }}
          tableHeight="calc(100vh - 360px)"
          noRowsMessage={
            searchQuery.trim()
              ? 'No users match your search.'
              : 'No users have direct access to this merchant yet.'
          }
          tableRef={tableRef}
          rowSx={(row) =>
            row.userId === selectedUserId ? { backgroundColor: 'action.selected' } : {}
          }
          renderPagination={() => (
            <Pagination
              itemCount={response?.data.length ?? 0}
              itemTotal={response?.metadata.totalRecords ?? 0}
              pageCount={response?.metadata.totalPages ?? 0}
              onPageChange={(_, page) => {
                setPageNumber(page);
                tableRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              page={pageNumber}
              pageSize={pageSize}
              pageSizeOptions={[10, 25, 50]}
              onPageSizeChange={(newPageSize) => {
                setPageNumber(1);
                setPageSize(newPageSize);
              }}
              disabled={isFetching}
            />
          )}
        />
      </Stack>

      {merchantId && canManagePortalUsers ? (
        <GrantMerchantAccessDialog
          isOpen={isGrantDialogOpen}
          merchantId={merchantId}
          onClose={() => setIsGrantDialogOpen(false)}
          onGranted={refetch}
        />
      ) : null}

      {formMode && (formMode === 'add' || selectedUserDetail?.userId === selectedUserId) && (
        <PortalUserFormDialog
          isOpen
          mode={formMode}
          roles={roles}
          user={formMode === 'edit' ? selectedUserDetail : undefined}
          initialMerchants={formMode === 'add' ? initialMerchants : undefined}
          onClose={() => {
            setFormMode(null);
            if (formMode === 'add') {
              setSelectedUserId(undefined);
            }
          }}
          onSubmit={async (body) => {
            try {
              if (formMode === 'add') {
                const created = await createPortalUser(body as CreatePortalUserBody).unwrap();
                toast.success('Portal user created.');
                setSelectedUserId(created.userId);
                refetch();
              } else if (selectedUserId) {
                await updatePortalUser({
                  userId: selectedUserId,
                  body: body as UpdatePortalUserBody,
                }).unwrap();
                toast.success('Portal user updated.');
                refetch();
              }
            } catch (error) {
              const message =
                error && typeof error === 'object' && 'data' in error
                  ? String((error as { data?: { message?: string } }).data?.message ?? '')
                  : '';
              throw new Error(message || 'Unable to save portal user.');
            }
          }}
        />
      )}

      <ActionDialogModal
        isOpen={!!revokeTarget}
        onProceed={handleRevoke}
        isSubmitting={isRevoking}
        onClose={() => setRevokeTarget(undefined)}
        content={{
          heading: 'Remove merchant access?',
          helpingText: revokeTarget
            ? getRevokeMerchantAccessHelpingText(revokeTarget.email, revokeTarget.merchantCount)
            : undefined,
          cancelButtonText: 'Cancel',
          proceedButtonText: 'Remove access',
        }}
      />

      <ActionDialogModal
        isOpen={!!deleteUserId}
        onProceed={handleDelete}
        isSubmitting={isDeleting}
        onClose={() => setDeleteUserId(undefined)}
        content={{
          heading: 'Delete this portal user?',
          helpingText:
            'This permanently deletes the portal user and removes their access to all merchants.',
          cancelButtonText: 'Cancel',
          proceedButtonText: 'Delete user',
        }}
      />
    </>
  );
};
